// src/pages/Dashboard.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../utils/config";
import { Card, CardContent } from "../components/ui/card";
import {
  BarChart as RBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { TrendingUp, User2, IndianRupee, Activity, Clock } from "lucide-react";

const COLORS = ["#60A5FA", "#34D399", "#FBBF24", "#F472B6", "#A78BFA", "#22D3EE"];

const formatCurrency = (num) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
    Number.isFinite(+num) ? +num : 0
  );

const SectionCard = ({ title, right, children, className = "" }) => (
  <Card className={`rounded-2xl bg-white/90 border border-slate-200 shadow-sm ${className}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{title}</h2>
        {right ? <div className="text-xs text-slate-500">{right}</div> : null}
      </div>
      <div className="mt-2 text-slate-800">{children}</div>
    </CardContent>
  </Card>
);

const Kpi = ({ icon: Icon, label, value, delta, deltaPos = true }) => (
  <SectionCard title={label}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon ? (
          <span className="h-9 w-9 rounded-xl bg-blue-50 ring-1 ring-blue-100 grid place-items-center">
            <Icon className="h-5 w-5 text-blue-600" />
          </span>
        ) : null}
        <p className="text-xl font-extrabold text-slate-900">{value}</p>
      </div>
      {typeof delta === "string" || typeof delta === "number" ? (
        <span className={`text-xs font-semibold ${deltaPos ? "text-emerald-600" : "text-rose-600"}`}>
          {delta}
        </span>
      ) : null}
    </div>
  </SectionCard>
);

const Dot = ({ tone = "slate" }) => (
  <span
    className={`inline-block h-2 w-2 rounded-full ${
      tone === "red" ? "bg-rose-500" : tone === "amber" ? "bg-amber-500" : tone === "green" ? "bg-emerald-500" : "bg-slate-400"
    }`}
  />
);

/* ------------------------ Normalization Helpers ------------------------ */

const ddmmyyyy = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}-${mm}-${yy}`;
};
const shortMonth = (d) => d.toLocaleString("en-US", { month: "short" }).toUpperCase();
const num = (v) => (v === null || v === undefined || v === "" ? 0 : +v || 0);

function normalizeDashboard(raw) {
  const r = raw || {};

  // ---- Top Orders ----
  const topOrdersRaw = r.topOrders || r.top_orders || r.top_orders_by_value || r.ordersTop || [];
  const topOrders = (topOrdersRaw || []).map((o, i) => ({
    id: o.id ?? o.order_id ?? i,
    customer: o.customer ?? o.customer_name ?? o.customerName ?? "-",
    date: ddmmyyyy(o.date ?? o.created_at ?? o.order_date),
    amount: num(o.amount ?? o.total ?? o.total_amount ?? o.value),
  }));

  // ---- Sales by Month (bar chart) ----
  const salesSrc =
    r.salesBarData || r.sales_bar_data || r.salesByMonth || r.sales_by_month || r.monthly_sales || [];
  const salesBarData = (salesSrc || []).map((d) => {
    let monthLabel =
      d.month ??
      d.label ??
      d.m ??
      (d.month_num && d.year ? shortMonth(new Date(d.year, (d.month_num || 1) - 1, 1)) : "");
    if (typeof monthLabel === "number") {
      monthLabel = shortMonth(new Date(new Date().getFullYear(), monthLabel - 1, 1));
    }
    return { month: String(monthLabel), orders: num(d.orders ?? d.value ?? d.total) };
  });

  // ---- Payment Modes (pie) ----
  const pmSrc = r.paymentPieData || r.payment_pie_data || r.paymentsByMode || r.payments_by_mode || [];
  const paymentPieData = (pmSrc || [])
    .map((d) => ({ name: d.name ?? d.mode ?? d.label ?? "", value: num(d.value ?? d.amount ?? d.total) }))
    .filter((x) => x.value > 0);

  // ---- Top dues (amounts arrive as strings) ----
  const topDues = (r.topDues || r.top_customer_dues || []).map((t) => ({
    customer: t.customer,
    amount: num(t.amount),
  }));

  // ---- Recent payments (amount & date) ----
  const recentPayments = (r.recentPayments || r.recent_payments || []).map((p, i) => ({
    id: i,
    customer: p.customer,
    amount: num(p.amount),
    payment_mode: p.payment_mode,
    date: ddmmyyyy(p.date || p.created_at),
  }));

  return {
    monthlyOrders: num(r.monthlyOrders ?? r.orders_this_month),
    lastMonthOrders: num(r.lastMonthOrders ?? r.orders_last_month),
    topOrders,
    salespersonOfMonth: r.salespersonOfMonth ?? r.salesperson_of_month ?? {},
    commissionSummary:
      r.commissionSummary ??
      r.commission_summary ?? { total: 0, paid: 0, due: 0 },
    topDoctors: r.topDoctors ?? r.top_commission_doctors ?? [],
    lowStock: r.lowStock ?? r.low_stock ?? [],
    expiringBatches: r.expiringBatches ?? r.expiring_batches ?? [],
    outstanding: num(r.outstanding ?? r.outstanding_payments),
    topDues,
    recentPayments,
    visitsToday: r.visitsToday ?? r.visits_today ?? [],
    activeCustomers: num(r.activeCustomers ?? r.active_this_week),
    inactiveCustomers: num(r.inactiveCustomers ?? r.inactive),
    salesBarData,
    paymentPieData,
  };
}
/* ---------------------------------------------------------------------- */

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const token = localStorage.getItem("token");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(normalizeDashboard(res.data));
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />
        ))}
      </div>
    );
  }
  if (error) return <div className="p-6 text-rose-600">{error}</div>;

  const {
    monthlyOrders: _monthlyOrders = 0,
    lastMonthOrders: _lastMonthOrders = 0,
    topOrders = [],
    salespersonOfMonth = {},
    commissionSummary = { total: 0, paid: 0, due: 0 },
    topDoctors = [],
    lowStock = [],
    expiringBatches = [],
    outstanding = 0,
    topDues = [],
    recentPayments = [],
    visitsToday = [],
    activeCustomers = 0,
    inactiveCustomers = 0,
    salesBarData = [],
    paymentPieData = [],
  } = data || {};

  const monthlyOrders = +_monthlyOrders || 0;
  const lastMonthOrders = +_lastMonthOrders || 0;
  const growth =
    lastMonthOrders > 0 ? (((monthlyOrders - lastMonthOrders) / lastMonthOrders) * 100).toFixed(1) + "%" : "—";
  const isGrowthPositive = lastMonthOrders > 0 && monthlyOrders >= lastMonthOrders;

  const salesData = (salesBarData || []).map((d) => ({
    month: d.month ?? "",
    orders: +d.orders || 0,
  }));

  const paymentData = (paymentPieData || [])
    .map((d) => ({ name: d.name ?? "", value: +d.value || 0 }))
    .filter((d) => d.value > 0);

  const expiryTone = (dateStr) => {
    if (!dateStr) return "slate";
    const d = new Date(dateStr);
    const now = new Date();
    const endOfThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const in90 = new Date(now.getFullYear(), now.getMonth() + 3, now.getDate());
    if (d <= endOfThisMonth) return "red";
    if (d <= in90) return "amber";
    return "green";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Kpi icon={TrendingUp} label="Orders This Month" value={monthlyOrders} delta={growth} deltaPos={isGrowthPositive} />
        <Kpi
          icon={User2}
          label="Salesperson of the Month"
          value={salespersonOfMonth?.name || "—"}
          delta={salespersonOfMonth?.orders ? `${salespersonOfMonth.orders} orders` : ""}
          deltaPos
        />
        <Kpi icon={IndianRupee} label="Outstanding Payments" value={formatCurrency(outstanding)} />

        <SectionCard title="Top Orders" className="md:col-span-2">
          {topOrders.length === 0 ? (
            <p className="text-sm text-slate-500">No orders yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {topOrders.map((o) => (
                <li key={o.id} className="flex items-center justify-between text-sm">
                  <span className="truncate mr-2">
                    {o.customer} <span className="text-slate-400">• {o.date}</span>
                  </span>
                  <span className="font-semibold">{formatCurrency(o.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Commission Summary">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="rounded-xl bg-blue-50 text-blue-900 ring-1 ring-blue-100 p-3">
              <p className="text-xs uppercase tracking-wider">Total</p>
              <p className="mt-1 font-bold">{formatCurrency(commissionSummary.total)}</p>
            </div>
            <div className="rounded-xl bg-emerald-50 text-emerald-900 ring-1 ring-emerald-100 p-3">
              <p className="text-xs uppercase tracking-wider">Paid</p>
              <p className="mt-1 font-bold">{formatCurrency(commissionSummary.paid)}</p>
            </div>
            <div className="rounded-xl bg-rose-50 text-rose-900 ring-1 ring-rose-100 p-3">
              <p className="text-xs uppercase tracking-wider">Due</p>
              <p className="mt-1 font-bold">{formatCurrency(commissionSummary.due)}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Top Commission Doctors"  className="md:col-span-2">
  {topDoctors.length === 0 ? (
    <p className="text-sm text-slate-500">No data.</p>
  ) : (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="text-left border-b">
          <th className="font-medium py-1">Doctor</th>
          <th className="font-semibold py-1 text-right">Total</th>
          <th className="font-semibold py-1 text-right">Paid</th>
          <th className="font-semibold py-1 text-right">Due</th>
        </tr>
      </thead>

      <tbody>
        {topDoctors.map((doc) => (
          <tr key={doc.doctor_id} className="border-b last:border-b-0">
            <td className="font-medium py-1">
              {doc.doctor_name}
            </td>

            <td className="text-right py-1">
              ₹{Number(doc.total).toFixed(2)}
            </td>

            <td className="text-right py-1 text-green-600">
              ₹{Number(doc.paid).toFixed(2)}
            </td>

            <td className="text-right py-1 text-red-600">
              ₹{Number(doc.due).toFixed(2)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )}
</SectionCard>

        <SectionCard title="Low Stock Products">
          {lowStock.length === 0 ? (
            <p className="text-sm text-slate-500">All good.</p>
          ) : (
            <ul className="space-y-1.5">
              {lowStock.map((p) => (
                <li key={p.product} className="flex items-center justify-between text-sm">
                  <span className="truncate flex items-center gap-2">
                    <Dot tone={p.stock <= 0 ? "red" : p.stock <= 5 ? "amber" : "slate"} />
                    {p.product}
                  </span>
                  <span className="font-medium">{p.stock} units</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Expiring Batches">
          {expiringBatches.length === 0 ? (
            <p className="text-sm text-slate-500">No near-expiry batches.</p>
          ) : (
            <ul className="space-y-1.5">
              {expiringBatches.map((b) => (
                <li key={`${b.product}-${b.expiry}`} className="flex items-center justify-between text-sm">
                  <span className="truncate flex items-center gap-2">
                    <Dot tone={expiryTone(b.expiry)} />
                    {b.product}
                  </span>
                  <span className="text-slate-600">{b.expiry}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Recent Payments">
          {recentPayments.length === 0 ? (
            <p className="text-sm text-slate-500">No recent payments.</p>
          ) : (
            <ul className="space-y-1.5">
              {recentPayments.map((p) => (
                <li key={p.id} className="flex items-center justify-between text-sm">
                  <span className="truncate">
                    {p.customer}{" "}
                    <span className="text-slate-400">
                      • {p.payment_mode || "mode"} • {p.date}
                    </span>
                  </span>
                  <span className="font-semibold">{formatCurrency(p.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Top Customer Dues">
          {topDues.length === 0 ? (
            <p className="text-sm text-slate-500">No dues.</p>
          ) : (
            <ul className="space-y-1.5">
              {topDues.map((c, i) => (
                <li key={`${c.customer}-${i}`} className="flex items-center justify-between text-sm">
                  <span className="truncate">{c.customer}</span>
                  <span className="font-semibold">{formatCurrency(c.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Sales Overview" className="md:col-span-2">
          <div className="h-[220px] -mx-1">
            {salesData.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-slate-500">No sales data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RBarChart data={salesData} margin={{ left: 12, right: 12 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(148,163,184,0.15)" }} />
                  <Bar dataKey="orders" radius={[6, 6, 0, 0]} fill="#60A5FA" />
                </RBarChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Payment Modes">
          <div className="h-[220px]">
            {paymentData.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-slate-500">No payment data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={paymentData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={72} innerRadius={36}>
                    {paymentData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Visits Today">
          {visitsToday.length === 0 ? (
            <p className="text-sm text-slate-500">No visits marked today.</p>
          ) : (
            <ul className="space-y-1.5">
   {visitsToday.map((v) => (
  <li key={v.id} className="flex items-center justify-between text-sm">
    <span className="truncate">
      <span className="font-medium">{v.user_name}</span>
      <span className="text-slate-400"> • {v.customer}</span>
    </span>
    <span className="text-slate-600">
      {v.time} • {Number(v.distance_km).toFixed(2)} km
    </span>
  </li>
))}


            </ul>
          )}
        </SectionCard>

        <SectionCard title="Customer Activity">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl bg-emerald-50 text-emerald-900 ring-1 ring-emerald-100 p-3">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                <p className="font-semibold">Active this week</p>
              </div>
              <p className="mt-1 text-xl font-extrabold">{activeCustomers}</p>
            </div>
            <div className="rounded-xl bg-slate-100 text-slate-900 ring-1 ring-slate-200 p-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <p className="font-semibold">Inactive</p>
              </div>
              <p className="mt-1 text-xl font-extrabold">{inactiveCustomers}</p>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default Dashboard;
